import React from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { Accordion, AccordionDetails, AccordionSummary, Grid2 } from '@mui/material'
import { groupBy, keys } from 'lodash'
import SimpleTabList from 'components/list/SimpleTabList'
import { shallowEqual, useSelector } from 'react-redux'
import { ACTION_COLOR, SORT_BY } from 'station/constants/CampaignConstants'
import { getI18nTitleDataLength, i18nize } from 'utils/StringUtil'
import { getLabel } from 'utils/StoreUtils'
import { CAMPAIGN_STATUT_FOR_FILTER, CAMPAIGN_TYPES } from 'campaign/constants/CampaignConstants'
import { hasValue } from 'utils/NumberUtil'
import { getYearOrString } from 'utils/DateUtil'
import { mediumGrey } from 'utils/constants/ColorTheme'
import DtoCampaignWithStats from 'pgsse/dto/DtoPgsseCampaignWithStats'
import PgsseCampaignCard from './PgsseCampaignCard'
import NaturePeopleIcon from '@mui/icons-material/NaturePeople'

const AccordionDisplay = ({
    groupCard = [],
    title = '',
    color = '',
    openFirst = false,
}) => {
    return (
        <Accordion defaultExpanded={openFirst}>
            <AccordionSummary color={color}>
                {title} {`(${groupCard.length} ${getI18nTitleDataLength(i18n.element, i18n.elements, groupCard.length)})`}
            </AccordionSummary>
            <AccordionDetails style={{ padding: 0 }}>
                {groupCard}
            </AccordionDetails>
        </Accordion>
    )
}

AccordionDisplay.propTypes = {
    groupCard: PropTypes.arrayOf(PropTypes.element),
    title: PropTypes.string,
    color: PropTypes.string,
    openFirst: PropTypes.bool,
}

const GroupCampaignByStatusDashboard = ({
    campaigns = [],
}) => {
    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const statusGrouped = groupBy(campaigns, 'status')
    // grouping by status then return one campaign card for each campaign in each status
    return keys(statusGrouped).map((key) =>
        statusGrouped[key].map(camp => {
            const contact = contacts.find(c => campaigns.contactCode === c.code)
            return (
                <PgsseCampaignCard
                    key={camp.name}
                    campaign={camp}
                    contact={contact}
                />
            )
        }),
    )
}

GroupCampaignByStatusDashboard.propTypes = {
    campaigns: PropTypes.arrayOf(PropTypes.instanceOf(DtoCampaignWithStats)),
}

const GroupCampaignByStatus = ({
    campaigns = [],
}) => {
    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const statusGrouped = groupBy(campaigns, 'status')
    const statusKeys = keys(statusGrouped)
    return statusKeys.map((key, i) => {
        const campaignCards = statusGrouped[key].map(camp => {
            const contact = contacts.find(c => campaigns.contactCode === c.code)
            return (
                <PgsseCampaignCard
                    key={camp.name}
                    campaign={camp}
                    contact={contact}
                />
            )
        })
        return (
            <Grid2 key={key} sx={{ marginBottom: i === (statusKeys.length - 1) ? 0 : '0.65rem' }}>
                <AccordionDisplay
                    groupCard={campaignCards}
                    title={i18n[getLabel(CAMPAIGN_STATUT_FOR_FILTER, key) || 'notDefined']}
                    color={ACTION_COLOR[parseInt(key)] || mediumGrey}
                    openFirst={i === 0}
                />
            </Grid2>
        )
    })
}

GroupCampaignByStatus.propTypes = {
    campaigns: PropTypes.arrayOf(PropTypes.instanceOf(DtoCampaignWithStats)),
}

const GroupCampaignByReferent = ({
    campaigns = [],
}) => {
    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const contactGrouped = groupBy(campaigns, c => hasValue(c.contactCode) ? getLabel(contacts, c.contactCode) : i18n.unknownReferent)
    const contactKeys = Object.keys(contactGrouped)
    return contactKeys.map((key, i) => {
        const campaignCards = contactGrouped[key].map(camp => {
            const contact = contacts.find(c => campaigns.contactCode === c.code)
            return (
                <PgsseCampaignCard
                    key={camp.name}
                    campaign={camp}
                    contact={contact}
                />
            )
        })
        return (
            <Grid2 key={key} style={{ marginBottom: i === contactKeys.length - 1 ? 0 : 10 }}>
                <AccordionDisplay
                    groupCard={campaignCards}
                    title={key}
                    openFirst={i === 0}
                />
            </Grid2>
        )
    })
}

GroupCampaignByReferent.propTypes = {
    campaigns: PropTypes.arrayOf(PropTypes.instanceOf(DtoCampaignWithStats)),
}

const GroupCampaignByDate = ({
    campaigns = [],
}) => {
    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const yearGrouped = groupBy(campaigns, c => getYearOrString(c.startDate, i18n.thisYear))
    const yearKeys = Object.keys(yearGrouped)
    return yearKeys.map((key, i) => {
        const campaignCards = yearGrouped[key].map(camp => {
            const contact = contacts.find(c => campaigns.contactCode === c.code)
            return (
                <PgsseCampaignCard
                    key={camp.name}
                    campaign={camp}
                    contact={contact}
                />
            )
        })
        return (
            <Grid2 key={key} style={{ marginBottom: i === 0 ? 0 : 10 }}>
                <AccordionDisplay
                    groupCard={campaignCards}
                    title={key}
                    openFirst={i === yearKeys.length - 1}
                />
            </Grid2>
        )
    }).toReversed()
}

GroupCampaignByDate.propTypes = {
    campaigns: PropTypes.arrayOf(PropTypes.instanceOf(DtoCampaignWithStats)),
}

const GroupCampaignByType = ({
    campaigns = [],
}) => {
    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)
    const campaignTypeGrouped = groupBy(campaigns, ({ campaignType = 4 }) => campaignType)
    const typeKeys = Object.keys(campaignTypeGrouped)
    return keys.map((key, i) => {
        const campaignCards = campaignTypeGrouped[key].map(camp => {
            const contact = contacts.find(c => campaigns.contactCode === c.code)
            return (
                <PgsseCampaignCard
                    key={camp.name}
                    campaign={camp}
                    contact={contact}
                />
            )
        })
        const title = getLabel(i18nize(CAMPAIGN_TYPES), key) || i18n.unknownType
        return (
            <Grid2 key={key} style={{ marginBottom: i === typeKeys.length - 1 ? 0 : 10 }}>
                <AccordionDisplay
                    groupCard={campaignCards}
                    title={title}
                    openFirst={i === 0}
                />
            </Grid2>
        )
    })
}

GroupCampaignByType.propTypes = {
    campaigns: PropTypes.arrayOf(PropTypes.instanceOf(DtoCampaignWithStats)),
}


const CampaignTabs = ({
    campaigns = [],
    sortBy = 0,
    setSortBy = () => { },
}) => {
    return (
        <SimpleTabList
            controlTab={sortBy}
            setControlTab={setSortBy}
            tabs={[{
                constant: SORT_BY.STATUS,
                label: i18n.byStatus,
                icon: 'edit',
            }, {
                constant: SORT_BY.DATE,
                label: i18n.byDate,
                icon: 'insert_invitation',
            }, {
                constant: SORT_BY.REFERENT,
                label: i18n.byReferent,
                icon: 'person',
            }, {
                constant: SORT_BY.TYPE,
                label: i18n.byCampaignType,
                icon: 'business_center',
            }]}
        >
            {tab => (!campaigns.length ? (
                <Grid2 sx={{ padding: '10%' }}>
                    <Grid2 className='text-align-center'>
                        <NaturePeopleIcon sx={{ fontSize: '4rem' }} />
                    </Grid2>
                    <Grid2 className='center font-size-20'>{i18n.NoneResultForThisResearch}</Grid2>
                </Grid2>
            ) : (
                <>
                    {tab === SORT_BY.STATUS && <GroupCampaignByStatus campaigns={campaigns} />}
                    {tab === SORT_BY.REFERENT && <GroupCampaignByReferent campaigns={campaigns} />}
                    {tab === SORT_BY.TYPE && <GroupCampaignByType campaigns={campaigns} />}
                    {tab === SORT_BY.DATE && <GroupCampaignByDate campaigns={campaigns} />}
                </>
            ))}
        </SimpleTabList>
    )
}

CampaignTabs.propTypes = {
    campaigns: PropTypes.arrayOf(PropTypes.instanceOf(DtoCampaignWithStats)),
    sortBy: PropTypes.number,
    setSortBy: PropTypes.func,
}

export { CampaignTabs, GroupCampaignByStatus, GroupCampaignByStatusDashboard }